import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ConfirmActionDialog } from '@/components/ui/confirm-action-dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { formatDeleteError } from '@/lib/api';

export default function ActivitiesListPage() {
  const [activities, setActivities] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [blocks, setBlocks] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [projectFilter, setProjectFilter] = useState('');
  const [blockFilter, setBlockFilter] = useState('');
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState({ projectId: '', blockId: '', name: '', defaultUnitId: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [toggling, setToggling] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<{ item: any; action: 'activate' | 'deactivate' } | null>(null);
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const canDelete = hasPermission('activities:delete');

  useEffect(() => {
    api.get<{ data: any[] }>('/projects?availability=active').then(r => setProjects(r.data || []));
    api.get('/shifts').then(() => {});
    loadUnits();
  }, []);

  useEffect(() => { if (form.projectId) loadBlocks(form.projectId); }, [form.projectId]);
  useEffect(() => { loadActivities(); }, [projectFilter, blockFilter, statusFilter]);

  async function loadUnits() {
    try { const res = await api.get<{ data: any[] }>('/units/all'); setUnits(res.data || []); } catch {}
  }

  async function loadBlocks(projectId: string) {
    try { const res = await api.get<{ data: any[] }>(`/blocks?projectId=${projectId}`); setBlocks(res.data || []); } catch { setBlocks([]); }
  }

  async function loadActivities() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (projectFilter && projectFilter !== 'all') params.set('projectId', projectFilter);
      if (blockFilter && blockFilter !== 'all') params.set('blockId', blockFilter);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const res = await api.get<{ data: any[] }>(`/activities?${params.toString()}`);
      setActivities(res.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function handleSave() {
    if (!form.projectId || !form.blockId || !form.name.trim() || !form.defaultUnitId) return;
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await api.put(`/activities/${editing.id}`, { name: form.name.trim(), blockId: form.blockId, defaultUnitId: form.defaultUnitId });
      } else {
        await api.post('/activities', { projectId: form.projectId, blockId: form.blockId, name: form.name.trim(), defaultUnitId: form.defaultUnitId });
      }
      setForm({ projectId: '', blockId: '', name: '', defaultUnitId: '' });
      setEditing(null);
      addToast(editing ? 'Updated' : 'Created', 'success');
      await loadActivities();
    } catch (e: any) { setError(e?.message || 'Failed'); }
    setSaving(false);
  }

  function handleCancel() {
    setForm({ projectId: '', blockId: '', name: '', defaultUnitId: '' });
    setEditing(null);
    setError('');
  }

  const handleDelete = useCallback(async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/activities/${deleteTarget.id}`);
      addToast('Activity permanently deleted.', 'success');
      setDeleteTarget(null);
      await loadActivities();
    } catch (e: unknown) { addToast(formatDeleteError(e, 'activity'), 'error'); }
    setDeleting(false);
  }, [deleteTarget, deleting]);

  async function handleToggleStatus(item: any) {
    setToggling(item.id);
    try {
      await api.put(`/activities/${item.id}`, { isActive: !item.is_active });
      addToast(item.is_active ? `${item.name} deactivated successfully.` : `${item.name} activated successfully.`, 'success');
      await loadActivities();
    } catch (e: any) { addToast(e?.message || 'Failed', 'error'); }
    setToggling(null);
  }

  const formValid = form.projectId && form.blockId && form.name.trim() && form.defaultUnitId;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-heading font-bold text-primary">Activities</h1>

      <Card>
        <CardHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
            <div className="space-y-1">
              <label className="text-sm font-medium">Project</label>
              <Select value={form.projectId} onValueChange={v => setForm(f => ({ ...f, projectId: v, blockId: '', defaultUnitId: '' }))}>
                <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>{projects.map((p: any) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Block</label>
              <Select value={form.blockId} onValueChange={v => setForm(f => ({ ...f, blockId: v, defaultUnitId: '' }))} disabled={!form.projectId}>
                <SelectTrigger><SelectValue placeholder="Select block" /></SelectTrigger>
                <SelectContent>{blocks.map((b: any) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Activity Name</label>
              <Input placeholder="Activity name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Unit of Measurement</label>
              <Select value={form.defaultUnitId} onValueChange={v => setForm(f => ({ ...f, defaultUnitId: v }))} disabled={!form.projectId || !form.blockId}>
                <SelectTrigger><SelectValue placeholder="Select unit" /></SelectTrigger>
                <SelectContent>{units.map((u: any) => <SelectItem key={u.id} value={u.id}>{u.symbol ? `${u.name} (${u.symbol})` : u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={saving || !formValid}>
                {editing ? 'Update' : <><Plus size={16} className="mr-1" />Add</>}
              </Button>
              {editing && (
                <Button variant="outline" onClick={handleCancel}>Cancel</Button>
              )}
            </div>
          </div>
          {error && <p className="text-sm text-error mt-2">{error}</p>}
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 mb-4">
            <Select value={projectFilter} onValueChange={v => { setProjectFilter(v || ''); setBlockFilter(''); }}>
              <SelectTrigger className="w-48"><SelectValue placeholder="All Projects" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map((p: any) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={blockFilter} onValueChange={v => setBlockFilter(v || '')}>
              <SelectTrigger className="w-48"><SelectValue placeholder="All Blocks" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Blocks</SelectItem>
                {blocks.map((b: any) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              {(['all', 'active', 'inactive'] as const).map(v => (
                <Button key={v} type="button" size="sm" variant={statusFilter === v ? 'default' : 'outline'} onClick={() => setStatusFilter(v)}>
                  {v === 'all' ? 'All' : v === 'active' ? 'Active' : 'Inactive'}
                </Button>
              ))}
            </div>
          </div>
          {loading ? <p className="text-center py-8 text-secondary">Loading...</p> : activities.length === 0 ? (
            <p className="text-center py-8 text-secondary">No activities found</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="border-b"><th className="text-left py-3">Name</th><th className="text-left py-3">Project</th><th className="text-left py-3">Block</th><th className="text-left py-3">Unit</th><th className="text-left py-3">Status</th><th className="text-right py-3">Actions</th></tr></thead>
              <tbody>{activities.map((a: any) => (
                <tr key={a.id} className="border-b hover:bg-hover transition-colors">
                  <td className="py-3 font-medium">{a.name}</td>
                  <td className="py-3">{a.project_name}</td>
                  <td className="py-3">{a.block_name}</td>
                  <td className="py-3">{a.default_unit_name ? (a.default_unit_symbol ? `${a.default_unit_name} (${a.default_unit_symbol})` : a.default_unit_name) : <span className="text-secondary italic">Unit not assigned</span>}</td>
                  <td className="py-3"><Badge variant={a.is_active ? 'approved' : 'inactive'}>{a.is_active ? 'Active' : 'Inactive'}</Badge></td>
                  <td className="py-3 text-right space-x-1">
                    {a.is_active ? (
                      <Button variant="ghost" size="sm" className="text-warning hover:text-warning" disabled={toggling === a.id} onClick={() => setConfirmTarget({ item: a, action: 'deactivate' })}>
                        Deactivate
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" className="text-success hover:text-success" disabled={toggling === a.id} onClick={() => setConfirmTarget({ item: a, action: 'activate' })}>
                        Activate
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => { setEditing(a); setForm({ projectId: a.project_id, blockId: a.block_id, name: a.name, defaultUnitId: a.default_unit_id || '' }); }} title="Edit"><Pencil size={14} /></Button>
                    {canDelete && (
                      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setDeleteTarget(a)} aria-label={`Permanently delete ${a.name}`} title="Permanently delete"><Trash2 size={14} /></Button>
                    )}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete Activity?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to permanently delete <strong>{deleteTarget?.name}</strong> from block <strong>{deleteTarget?.block_name}</strong>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white" onClick={handleDelete}>
              {deleting ? 'Deleting...' : 'Permanently Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConfirmActionDialog
        open={!!confirmTarget && confirmTarget.action === 'deactivate'}
        onOpenChange={(o) => { if (!o) setConfirmTarget(null); }}
        title={`Deactivate ${confirmTarget?.item?.name}?`}
        description="This record will remain in existing records and reports, but it will not be available for new selections."
        actionLabel="Deactivate"
        buttonStyle="amber"
        loading={!!confirmTarget && toggling === confirmTarget.item?.id}
        loadingLabel="Deactivating..."
        onConfirm={() => { const t = confirmTarget; setConfirmTarget(null); if (t) handleToggleStatus(t.item); }}
      />

      <ConfirmActionDialog
        open={!!confirmTarget && confirmTarget.action === 'activate'}
        onOpenChange={(o) => { if (!o) setConfirmTarget(null); }}
        title={`Activate ${confirmTarget?.item?.name}?`}
        description="This record will become available for new selections."
        actionLabel="Activate"
        buttonStyle="green"
        loading={!!confirmTarget && toggling === confirmTarget.item?.id}
        loadingLabel="Activating..."
        onConfirm={() => { const t = confirmTarget; setConfirmTarget(null); if (t) handleToggleStatus(t.item); }}
      />
    </div>
  );
}
